import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getLiveChatConfig } from "@leagueplatform/live-chat-api";
import { useGetUserProfile } from "@leagueplatform/web-common";
import { useAppInitDetails } from "hooks/use-app-init-details/use-app-init-details.hook";
import {
  isEntitledForLiveChat,
  isOutsideHoursOfOperation,
} from "./live-chat-config";

const QUERY_KEY_BASE = ["emblem", "live-chat-availability"] as const;

// Member-scoped cache key — the underlying QueryClient is app-scoped
// and survives member switches (e.g. delete-account → new sign-in on
// the same tab, or member-selection swap on multi-member accounts).
// Without the userId segment, the previous member's cached
// `LIVE_CHAT_AVAILABLE` would remain served from `data` until the
// invalidated refetch settles, so a newly ineligible member could
// briefly retain the CTA. Keying by userId turns a member switch into
// a fresh query — `data` is undefined until the new fetch resolves,
// which the loading branch already renders as `isAvailable: false`.
const queryKeyFor = (userId: string | undefined) =>
  [...QUERY_KEY_BASE, userId ?? "anonymous"] as const;

/**
 * Client-side entry-state gate for the live-chat bubble. Reads
 * `GET /v1/live-chat-configs` and returns two independent signals:
 *
 *   - `isEntitled` — the member's LOB is serviced by live chat at all.
 *     Time-independent (EMBLEM-8962 split entitlement out of
 *     `LIVE_CHAT_AVAILABLE`). Drives bubble visibility: unentitled
 *     members never see the CTA; entitled members see it even outside
 *     the staffed window.
 *   - `isAvailable` — the queue is staffed right now. Flips with the
 *     ET-clock hours boundary. Preserved as the gate the bridge checks
 *     on the POST response.
 *
 * Semantics (must match Android's `GetLiveChatAvailabilityUseCase` + the
 * iOS equivalent on each key):
 *   - Only an explicit `"false"` (case-insensitive) closes either gate.
 *     Missing key / null / any other value / failed request all read as
 *     permissive. BE omits a key when it can't resolve the answer, and
 *     the AC requires an unresolved lookup not to hide the bubble.
 *   - Starts hidden (`isEntitled: false`, `isAvailable: false` while
 *     pending) so ineligible members never see the CTA flash before the
 *     answer arrives.
 *   - Re-asked on `visibilitychange → visible`. The hours boundary turns
 *     over on a clock the app doesn't watch, so a tab left open across
 *     it would keep showing a stale availability answer.
 *   - Gated on `useAppInitDetails` having settled — never issued before
 *     member context is loaded.
 */
export const useLiveChatAvailability = (): {
  isEntitled: boolean;
  isAvailable: boolean;
  isLoading: boolean;
} => {
  const { data: appInit } = useAppInitDetails();
  const { data: profile } = useGetUserProfile() as {
    data: { userId?: string } | undefined;
  };
  const userId = profile?.userId;
  // Both appInit AND userId must be present — without userId the query
  // would key on "anonymous" and cache-collide across sessions.
  const enabled = Boolean(appInit) && Boolean(userId);
  const queryClient = useQueryClient();

  const { data, status } = useQuery({
    queryKey: queryKeyFor(userId),
    queryFn: getLiveChatConfig,
    enabled,
    // Android parity — HOO changes on the extension side reflect within
    // 60s (or on tab defocus/refocus, which the visibilitychange
    // listener below already covers). Any shorter window would burn
    // config fetches without materially improving the UX: the CTA is
    // gated at page load and again on visibility change, so a stale
    // config surviving up to 60s only affects members who leave the tab
    // open across a HOO boundary — and the visibilitychange refetch
    // catches that when they return.
    staleTime: 60_000,
    // Failed request → treat as available (soft-failure default). Don't
    // retry — a repeatable failure would just keep the CTA hidden longer.
    retry: false,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        queryClient
          .invalidateQueries({ queryKey: queryKeyFor(userId) })
          .catch(() => undefined);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [enabled, queryClient, userId]);

  if (status === "loading") {
    // "Not asked yet" — hide until the first response settles.
    return { isEntitled: false, isAvailable: false, isLoading: true };
  }
  if (status === "error") {
    // Soft-failure default — matches Android. A transient GET failure
    // must not hide an entitled member's bubble; the authoritative
    // re-check still runs on `POST /v1/live-chat` at session start.
    return { isEntitled: true, isAvailable: true, isLoading: false };
  }

  // Delegate to the canonical helpers — same parse the bridge's
  // `handleUserStartChat` gate uses on the POST response, so the CTA
  // and the START_CHAT check can't drift apart on serialisation quirks
  // (boolean vs stringified boolean vs case variants).
  const config = data?.data as Record<string, unknown> | undefined;
  return {
    isEntitled: isEntitledForLiveChat(config),
    isAvailable: !isOutsideHoursOfOperation(config),
    isLoading: false,
  };
};
